import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './Components/Navbar';
import Hero from './Pages/Hero';
import About from './Pages/About';
import Services from './Pages/Services';
import Partners from './Pages/Partners';
import Properties from './Pages/Properties';
import Contact from './Pages/Contact';
import './App.css';
import Footer from "./Components/Footer";

/* ════════════════════════════════════════════
   SCROLL TO TOP on every route change
════════════════════════════════════════════ */
const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
    return null;
};

/* ════════════════════════════════════════════
   FOOTER
════════════════════════════════════════════ */


/* ════════════════════════════════════════════
   LAYOUT — Navbar + page content + Footer
════════════════════════════════════════════ */
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <>
        <Navbar />
        <main>{children}</main>
        <Footer />
    </>
);

/* ════════════════════════════════════════════
   PAGES
   Hero = Home (/)
   Each section = its own route
════════════════════════════════════════════ */

// /rent and /sale both load Properties but default to correct tab
const ForRent: React.FC  = () => <Properties defaultTab="rent" />;
const ForSale: React.FC  = () => <Properties defaultTab="sale" />;

/* ════════════════════════════════════════════
   APP INNER (needs Router context)
════════════════════════════════════════════ */
const AppInner: React.FC = () => (
    <>
        <ScrollToTop />
        <Routes>
            <Route path="/" element={
                <Layout><Hero /></Layout>
            } />
            <Route path="/about" element={
                <Layout><About /></Layout>
            } />
            <Route path="/services" element={
                <Layout><Services /></Layout>
            } />
            <Route path="/partners" element={
                <Layout><Partners /></Layout>
            } />
            <Route path="/rent" element={
                <Layout><ForRent /></Layout>
            } />
            <Route path="/sale" element={
                <Layout><ForSale /></Layout>
            } />
            <Route path="/contact" element={
                <Layout><Contact /></Layout>
            } />
            {/* 404 */}
            <Route path="*" element={
                <Layout>
                    <div style={{
                        minHeight: '60vh', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
                        fontFamily: 'var(--ff-h)', color: 'var(--ink2)',
                    }}>
                        <h1 style={{ fontSize: 'clamp(3rem,8vw,6rem)', fontWeight: 300, color: 'var(--teal)' }}>404</h1>
                        <p style={{ fontSize: '1.1rem', fontWeight: 300 }}>Page not found.</p>
                        <a href="/" style={{
                            fontFamily: 'var(--ff-ui)', fontSize: '0.72rem', fontWeight: 600,
                            letterSpacing: '0.18em', textTransform: 'uppercase',
                            background: 'var(--teal)', color: '#fff',
                            padding: '12px 28px', borderRadius: 'var(--r)', textDecoration: 'none',
                        }}>Back to Home</a>
                    </div>
                </Layout>
            } />
        </Routes>
    </>
);

/* ════════════════════════════════════════════
   ROOT APP
════════════════════════════════════════════ */
const App: React.FC = () => (
    <BrowserRouter>
        <ThemeProvider>
            <AppInner />
        </ThemeProvider>
    </BrowserRouter>
);

export default App;